import { Button, Checkbox, Group, Paper, Table, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useNotificationSettings, useSaveNotificationSettings, type Channels } from '@/api/account';
import { pushSupported, useBrowserPushState, useDisablePush, useEnablePush, usePushConfig, useTestPush } from '@/api/push';
import { useApiError } from '@/hooks/useApiError';

/** Benachrichtigungseinstellungen je Typ und Kanal (NOT-02). */
export function NotificationSettingsSection() {
  const { t } = useTranslation();
  const onError = useApiError();
  const { data } = useNotificationSettings();
  const save = useSaveNotificationSettings();
  if (!data) return null;
  const types = Object.keys(data).sort();
  const toggle = (type: string, channel: keyof Channels, value: boolean) =>
    save.mutate({ ...data, [type]: { ...data[type]!, [channel]: value } }, { onError });

  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        {t('notifications.settingsTitle')}
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        {t('notifications.settingsText')}
      </Text>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th />
            <Table.Th ta="center">{t('notifications.inApp')}</Table.Th>
            <Table.Th ta="center">{t('notifications.email')}</Table.Th>
            <Table.Th ta="center">{t('notifications.push')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {types.map((type) => (
            <Table.Tr key={type}>
              <Table.Td>{t(`notifications.types.${type}`, { defaultValue: type })}</Table.Td>
              {(['inApp', 'email', 'push'] as const).map((channel) => (
                <Table.Td key={channel}>
                  <Checkbox
                    mx="auto"
                    w="fit-content"
                    checked={data[type]![channel]}
                    onChange={(e) => toggle(type, channel, e.currentTarget.checked)}
                    aria-label={`${type} ${channel}`}
                  />
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <PushDevice />
    </Paper>
  );
}

/** Push auf diesem Gerät aktivieren (NOT-03). */
function PushDevice() {
  const { t } = useTranslation();
  const onError = useApiError();
  const { data: config } = usePushConfig();
  const { data: active } = useBrowserPushState();
  const enable = useEnablePush();
  const disable = useDisablePush();
  const test = useTestPush();
  if (!pushSupported()) {
    return (
      <Text size="xs" c="dimmed" mt="sm">
        {t('notifications.pushUnsupported')}
      </Text>
    );
  }
  return (
    <Group mt="md" justify="space-between">
      <Text size="sm" c="dimmed">
        {active ? t('notifications.pushActive', { count: config?.subscriptions ?? 1 }) : t('notifications.pushInactive')}
      </Text>
      <Group gap="xs">
        {active ? (
          <>
            <Button size="xs" variant="default" loading={test.isPending}
              onClick={() => test.mutate(undefined, { onSuccess: () => notifications.show({ message: t('notifications.pushSent') }), onError })}>
              {t('notifications.pushTest')}
            </Button>
            <Button size="xs" variant="subtle" color="red" loading={disable.isPending} onClick={() => disable.mutate(undefined, { onError })}>
              {t('notifications.pushDisable')}
            </Button>
          </>
        ) : (
          <Button
            size="xs"
            disabled={!config}
            loading={enable.isPending}
            onClick={() =>
              enable.mutate(config!.publicKey, {
                onError: (e) =>
                  e instanceof Error && e.message === 'permission-denied'
                    ? notifications.show({ color: 'yellow', message: t('notifications.pushDenied') })
                    : onError(e),
              })
            }
          >
            {t('notifications.pushEnable')}
          </Button>
        )}
      </Group>
    </Group>
  );
}
